import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import { ROUTES } from 'config/navConfig';
import CapitalStructureTab from './components/CapitalStructureTab';
import WaterfallReturnsTab from './components/WaterfallReturnsTab';
import CoInvestmentTab from './components/CoInvestmentTab';
import FeesEconomicsTab from './components/FeesEconomicsTab';
import LegalStructureTab from './components/LegalStructureTab';
import TermSheetTab from './components/TermSheetTab';

const DEALS_DATA = {
  'deal-1': { id: 'deal-1', name: 'Mersey Tidal Array', structureType: 'Senior Equity', status: 'In Review', totalDealSize: 120, equityTranche: 48, equityPct: 40, debtTranche: 72, debtPct: 60, targetIRR: 14.2, hurdleRate: 8, carry: 20 },
  'deal-2': { id: 'deal-2', name: 'Humber Offshore Wind', structureType: 'Blended', status: 'Approved', totalDealSize: 95, equityTranche: 38, equityPct: 40, debtTranche: 57, debtPct: 60, targetIRR: 12.8, hurdleRate: 7, carry: 17.5 },
  'deal-3': { id: 'deal-3', name: 'Pennine Wind Farm Phase 2', structureType: 'Club Deal', status: 'Draft', totalDealSize: 67, equityTranche: 40.2, equityPct: 60, debtTranche: 26.8, debtPct: 40, targetIRR: 15.5, hurdleRate: 9, carry: 20 },
  'deal-4': { id: 'deal-4', name: 'Southern Grid Upgrade', structureType: 'Co-Investment', status: 'Approved', totalDealSize: 52, equityTranche: 20.8, equityPct: 40, debtTranche: 31.2, debtPct: 60, targetIRR: 11.2, hurdleRate: 7, carry: 15 },
  'deal-5': { id: 'deal-5', name: 'Bristol Urban Transit', structureType: 'Mezzanine', status: 'In Review', totalDealSize: 74, equityTranche: 22.2, equityPct: 30, debtTranche: 51.8, debtPct: 70, targetIRR: 13.7, hurdleRate: 8.5, carry: 17.5 },
  'deal-7': { id: 'deal-7', name: 'Tyne Bridge Refinancing', structureType: 'Co-Investment', status: 'In Review', totalDealSize: 265, equityTranche: 85, equityPct: 32, debtTranche: 180, debtPct: 68, targetIRR: 10.8, hurdleRate: 8, carry: 20 },
};

const DEFAULT_DEAL = { id: 'new', name: 'New Deal Structure', structureType: 'Senior Equity', status: 'Draft', totalDealSize: 100, equityTranche: 40, equityPct: 40, debtTranche: 60, debtPct: 60, targetIRR: 14.0, hurdleRate: 8, carry: 20 };

const TABS = [
  { id: 'capital', label: 'Capital Structure', icon: 'Layers' },
  { id: 'waterfall', label: 'Waterfall & Returns', icon: 'GitBranch' },
  { id: 'coinvestment', label: 'Co-Investment & Syndication', icon: 'Users' },
  { id: 'fees', label: 'Fees & Economics', icon: 'Receipt' },
  { id: 'legal', label: 'Legal Structure', icon: 'Scale' },
  { id: 'termsheet', label: 'Term Sheet', icon: 'FileText' },
];

const STATUS_CONFIG = {
  Draft: { bg: 'bg-slate-100', text: 'text-slate-600' },
  'In Review': { bg: 'bg-amber-50', text: 'text-amber-700' },
  Approved: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  Archived: { bg: 'bg-slate-50', text: 'text-slate-500' },
};

const STRUCTURE_TYPE_CONFIG = {
  'Senior Equity': 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  Mezzanine: 'bg-indigo-50 text-indigo-700',
  Blended: 'bg-teal-50 text-teal-700',
  'Club Deal': 'bg-purple-50 text-purple-700',
  'Co-Investment': 'bg-cyan-50 text-cyan-700',
};

const USER_ROLE = 'admin'; // admin | sponsor | investor

export default function DealStructuringWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealId = searchParams?.get('dealId');
  const deal = DEALS_DATA?.[dealId] || DEFAULT_DEAL;

  const [activeTab, setActiveTab] = useState('capital');
  const [toasts, setToasts] = useState([]);
  const [showICModal, setShowICModal] = useState(false);
  const userRole = USER_ROLE;
  const readOnly = userRole === 'investor';

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now()?.toString();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev?.filter((t) => t?.id !== id)), 3500);
  }, []);

  const handleSaveDraft = () => showToast('Draft saved successfully', 'success');
  const handleExportPDF = () => showToast('Exporting PDF...', 'success');
  const handleSubmitIC = () => {
    setShowICModal(false);
    showToast('Submitted for IC review', 'success');
  };

  const handleTokenise = () => {
    const params = new URLSearchParams({
      dealName: deal?.name || '',
      structureType: deal?.structureType || '',
      totalDealSize: deal?.totalDealSize?.toString() || '',
      equityTranche: deal?.equityTranche?.toString() || '',
      targetIRR: deal?.targetIRR?.toString() || '',
      hurdleRate: deal?.hurdleRate?.toString() || '',
      carry: deal?.carry?.toString() || '',
      dealId: deal?.id || '',
    });
    navigate(`/rwa-tokenisation?${params?.toString()}&openPipeline=1`);
  };

  const statusCfg = STATUS_CONFIG?.[deal?.status] || STATUS_CONFIG?.Draft;
  const typeCfg = STRUCTURE_TYPE_CONFIG?.[deal?.structureType] || 'bg-slate-100 text-slate-600';

  return (
    <AppLayout userRole={userRole}>
      <div className="min-h-screen bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 py-8">

          {/* Read-only Banner */}
          {readOnly && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <Icon name="Eye" size={16} />
              <span>You are viewing this deal structure in <strong>read-only mode</strong> (Investor access). Editing is restricted.</span>
            </div>
          )}

          {/* Header */}
          <div className="mb-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <button onClick={() => navigate(ROUTES.structuringHub)} className="hover:text-foreground transition-colors">
                Deal Structuring
              </button>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground font-medium">{deal?.name}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-foreground">{deal?.name}</h1>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${typeCfg}`}>
                  {deal?.structureType}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusCfg?.bg} ${statusCfg?.text}`}>
                  {deal?.status}
                </span>
              </div>

              {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={handleSaveDraft}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg text-foreground hover:bg-muted transition-colors font-medium">
                    <Icon name="Save" size={14} /> Save Draft
                  </button>
                  {userRole === 'admin' && (
                    <button onClick={() => setShowICModal(true)}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium">
                      <Icon name="Send" size={14} /> Submit for IC Review
                    </button>
                  )}
                  <button
                    onClick={handleTokenise}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                  >
                    <Icon name="Coins" size={14} /> Tokenise
                  </button>
                  <button onClick={() => showToast('Generating term sheet...', 'success')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
                    <Icon name="FileText" size={14} /> Generate Term Sheet
                  </button>
                  <button onClick={handleExportPDF}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg text-foreground hover:bg-muted transition-colors font-medium">
                    <Icon name="Download" size={14} /> Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <div className="flex gap-0 overflow-x-auto">
              {TABS?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'capital' && <CapitalStructureTab deal={deal} readOnly={readOnly} />}
            {activeTab === 'waterfall' && <WaterfallReturnsTab deal={deal} readOnly={readOnly} />}
            {activeTab === 'coinvestment' && <CoInvestmentTab deal={deal} readOnly={readOnly} />}
            {activeTab === 'fees' && <FeesEconomicsTab deal={deal} readOnly={readOnly} />}
            {activeTab === 'legal' && <LegalStructureTab deal={deal} readOnly={readOnly} />}
            {activeTab === 'termsheet' && <TermSheetTab deal={deal} showToast={showToast} />}
          </div>

          <p className="mt-8 text-xs text-muted-foreground italic text-center">
            All data shown is sample data for demonstration purposes only and does not represent real transactions.
          </p>
        </div>
      </div>
      {/* IC Review Modal */}
      {showICModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Submit for IC Review</h3>
              <button onClick={() => setShowICModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              You are about to submit <strong>{deal?.name}</strong> for Investment Committee review. This will notify all IC members and change the status to "In Review".
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowICModal(false)} className="flex-1 px-4 py-2 border border-border rounded-lg text-sm text-foreground hover:bg-muted transition-colors">Cancel</button>
              <button onClick={handleSubmitIC} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors">Submit for IC Review</button>
            </div>
          </div>
        </div>
      )}
      {/* Toasts */}
      <div className="fixed bottom-6 right-6 z-50 space-y-2">
        {toasts?.map((t) => (
          <div key={t?.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            t?.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'
          }`}>
            <Icon name={t?.type === 'success' ? 'CheckCircle' : 'Info'} size={16} />
            {t?.message}
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
